import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { icon } from "../../../config";
import { FORM_ICONS_UPDATED } from "../FormsConstant";
import ColumnDropZone from "./ColumnDropZone";
import "./RowContainer.scss";

/**
 * RowContainer Component
 * Renders a multi-row, multi-column grid layout that can contain other form elements
 * @param {Object} element - The row container element data
 * @param {Function} onElementUpdate - Callback to update element data
 * @param {Object} selectedElement - Currently selected element
 * @param {Function} onElementSelect - Callback to select an element
 * @param {Function} onDrop - Callback for handling drops into grid cells
 * @param {Function} onElementDelete - Callback for deleting elements from grid cells
 */
const RowContainer = ({ element, onElementUpdate, selectedElement, onElementSelect, onDrop, onElementDelete, onRowDelete, onAddRow }) => {
  const { columnCount = 2, rowCount = 1, columnWidths = [50, 50], gap = 8 } = element.properties || {};
  const isSelected = selectedElement?.id === element.id;

  // Ensure counts are valid numbers
  const safeColumnCount = Math.max(1, Math.min(4, parseInt(columnCount) || 2));
  const safeRowCount = Math.max(1, parseInt(rowCount) || 1);

  // Ensure columnWidths array matches columnCount
  const safeColumnWidths = columnWidths.length === safeColumnCount ? columnWidths : Array(safeColumnCount).fill(100 / safeColumnCount);

  // Get element in specific grid cell
  const getElementInCell = (rowIndex, columnIndex) => {
    if (element.gridElements && element.gridElements[rowIndex] && element.gridElements[rowIndex][columnIndex]) {
      return element.gridElements[rowIndex][columnIndex];
    }
    return null;
  };

  // Handle click on row container
  const handleRowClick = (event) => {
    // Only select row if clicking on the row itself, not on child elements
    if (event.target === event.currentTarget || event.target.classList.contains("row-container-inner")) {
      event.stopPropagation();
      onElementSelect(element);
    }
  };

  // Handle drop into specific grid cell
  const handleCellDrop = (rowIndex, columnIndex, droppedComponent) => {
    if (onDrop) {
      onDrop(element.id, columnIndex, droppedComponent, { rowIndex, columnIndex });
    }
  };

  // Handle element update within grid cell - pass through to FormBuilder
  const handleCellElementUpdate = (elementId, updatedProperties) => {
    if (onElementUpdate) {
      // Pass the update directly to FormBuilder which now handles row container grid
      onElementUpdate(elementId, updatedProperties);
    }
  };

  // Handle element delete from grid cell
  const handleCellElementDelete = (elementId, rowIndex, columnIndex) => {
    if (onElementDelete) {
      onElementDelete(elementId, rowIndex, columnIndex);
    }
  };

  return (
    <div className={`grid-layout-container ${isSelected ? "selected" : ""}`} onClick={handleRowClick} data-element-id={element.id}>
      {/* Grid Layout Header */}
      <div className='grid-layout-header'>
        <div
          className='grid-header-content'
          onClick={(e) => {
            e.stopPropagation();
            onElementSelect(element);
          }}>
          <span className='element-type-icon'>{FORM_ICONS_UPDATED["row-container"]}</span>
          <span className='grid-title'>Grid Layout</span>
        </div>
      </div>

      {/* Grid Content */}
      <div
        className='row-container-inner'
        style={{
          display: "grid",
          gridTemplateColumns: safeColumnWidths.map((width) => `${width}%`).join(" "),
          gridTemplateRows: `repeat(${safeRowCount}, auto)`,
        }}>
        {/* Render grid cells */}
        {Array.from({ length: safeRowCount }).map((_, rowIndex) => {
          return (
            <>
              {Array.from({ length: safeColumnCount }).map((_, columnIndex) => (
                <>
                  <ColumnDropZone
                    key={`${rowIndex}-${columnIndex}`}
                    rowIndex={rowIndex}
                    columnIndex={columnIndex}
                    element={getElementInCell(rowIndex, columnIndex)}
                    onDrop={(droppedComponent) => handleCellDrop(rowIndex, columnIndex, droppedComponent)}
                    onElementSelect={onElementSelect}
                    selectedElement={selectedElement}
                    onElementUpdate={handleCellElementUpdate}
                    onElementDelete={handleCellElementDelete}
                  />
                </>
              ))}
            </>
          );
        })}
      </div>
      <div className='add-row-zone'>
        <Button
          id={`add-row-btn-${element.id}`}
          className='simple-add-row-btn'
          onClick={() => onAddRow(element)}
          title='Add Row to Grid'
          size='small'>
          + Add Row
        </Button>
      </div>
    </div>
  );
};

export default RowContainer;
